import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, CheckCircle2, Loader2, TrendingUp, TrendingDown,
  Minus as MinusIcon, Users, BadgeEuro, Trophy, ArrowRight,
} from "lucide-react";
import { haptic } from "@/lib/haptics";

const FIELD_LABELS = {
  siret: "SIRET",
  company: "Entreprise",
  address: "Adresse",
  postal_code: "Code postal",
  city: "Ville",
  phone: "Téléphone",
  email: "Email",
  title: "Fonction",
  gps_lat: "Coordonnées GPS",
  gps_lng: "Coordonnées GPS",
  sector: "Secteur (NAF)",
};

const HEALTH_META = {
  growth:  { label: "En croissance",   Icon: TrendingUp,    cls: "text-emerald-300", border: "border-emerald-500/40", bg: "bg-emerald-500/10" },
  stable:  { label: "Stable",          Icon: MinusIcon,     cls: "text-amber-300",   border: "border-amber-500/40",  bg: "bg-amber-500/10" },
  decline: { label: "En repli",        Icon: TrendingDown,  cls: "text-rose-300",    border: "border-rose-500/40",   bg: "bg-rose-500/10" },
  unknown: { label: "Non évaluée",     Icon: MinusIcon,     cls: "text-zinc-400",    border: "border-[var(--border)]", bg: "bg-[var(--surface-alt)]" },
};

function Reveal({ delay = 0, children, testId }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div
      data-testid={testId}
      className={`transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}
    >
      {children}
    </div>
  );
}

/**
 * Magic Prosperr 2.0 — Heetch Pro dark.
 * Purement informationnel : présente les données publiques disponibles
 * (Pappers + SIRENE). Le commercial décide ensuite quoi en faire.
 */
export default function EnrichmentCard({ prospect, lead, onApplied }) {
  prospect = prospect || lead;
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);

  if (!prospect) return null;

  const missing = Object.keys(FIELD_LABELS).filter((f) => {
    const v = prospect[f];
    return v === undefined || v === null || v === "";
  });
  const missingUnique = [...new Set(missing.map((f) => FIELD_LABELS[f]))];

  const hasPappersData = !!prospect?.pappers_data;

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post(`/enrich/${prospect.prospect_id}`);
      setResult(data);
      haptic("light");
      const n = Object.keys(data.suggested || {}).length + (data.deep ? 1 : 0);
      if (n === 0) toast.info("Aucune donnée publique supplémentaire trouvée.");
      else toast.success(data.deep ? "Magic Prosperr : données premium trouvées" : `${n} info${n > 1 ? "s" : ""} trouvée${n > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Service d'enrichissement indisponible");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!result) return;
    setApplying(true);
    try {
      const payload = { ...(result.suggested || {}), score: result.new_score };
      if (result.deep) payload.deep = result.deep;
      const { data } = await api.put(`/enrich/${prospect.prospect_id}/apply`, payload);
      haptic("success");
      toast.success("Fiche enrichie");
      onApplied?.(data.prospect);
      setResult(null);
    } catch {
      toast.error("Erreur lors de l'application");
    } finally {
      setApplying(false);
    }
  };

  // ── Already-enriched persistent card
  if (hasPappersData && !result) {
    return <PappersDetailsCard data={prospect.pappers_data} onRefresh={run} loading={loading} />;
  }

  // ── Empty state: all known + no pappers data
  if (missingUnique.length === 0 && !result) {
    return (
      <div className="px-5 mt-4">
        <div className="hp-card p-3 flex items-center gap-3" data-testid="enrichment-idle">
          <div className="w-9 h-9 rounded-lg border border-emerald-500/40 bg-emerald-500/10 grid place-items-center shrink-0">
            <CheckCircle2 className="w-4 h-4 text-emerald-300" />
          </div>
          <div className="text-xs text-[var(--text)] flex-1">
            <strong>Fiche complète</strong>
            <div className="text-zinc-500 mt-0.5">Lancez Magic Prosperr pour récupérer les données financières publiques.</div>
          </div>
          <button
            onClick={run}
            disabled={loading}
            data-testid="magic-run-compact"
            className="hp-btn hp-btn-primary h-9 px-4 text-xs shrink-0"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Enrichir
          </button>
        </div>
      </div>
    );
  }

  // ── Initial state: not run yet
  return (
    <div className="px-5 mt-4" data-testid="enrichment-card">
      {!result && (
        <div className="rounded-xl p-5 border border-emerald-500/40 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, rgba(0,220,130,0.20), rgba(0,220,130,0.04))" }}>
          <div className="brand-glow absolute inset-0 pointer-events-none opacity-60" />
          {/* Loading pulse */}
          {loading && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full border border-emerald-500/30 animate-ping" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border border-emerald-500/50 animate-ping" style={{ animationDelay: "300ms" }} />
            </div>
          )}
          <div className="relative flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500 border border-emerald-500 grid place-items-center shrink-0">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-base text-white">Magic Prosperr</div>
              <div className="text-xs text-zinc-300 mt-0.5">Chiffre d'affaires, dirigeants, effectifs et santé financière des bases publiques.</div>
            </div>
          </div>

          {missingUnique.length > 0 && (
            <div className="relative flex flex-wrap gap-1.5 mt-4">
              {missingUnique.slice(0, 6).map((m) => (
                <span key={m} className="text-[10px] bg-emerald-500/15 text-emerald-200 rounded-full px-2.5 py-0.5 border border-emerald-500/30 font-medium">
                  {m}
                </span>
              ))}
              {missingUnique.length > 6 && (
                <span className="text-[10px] text-zinc-400 px-1 py-0.5">+{missingUnique.length - 6}</span>
              )}
            </div>
          )}

          <button
            onClick={run}
            disabled={loading}
            data-testid="enrich-run"
            className="relative mt-5 hp-btn hp-btn-primary w-full h-12 text-sm"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Recherche en cours…</>
            ) : (
              <><Sparkles className="w-4 h-4" />Lancer Magic Prosperr<ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        </div>
      )}

      {/* Results */}
      {result && <ResultsView result={result} onApply={apply} onDismiss={() => setResult(null)} applying={applying} />}
    </div>
  );
}

function ResultsView({ result, onApply, onDismiss, applying }) {
  const deep = result.deep;
  const scoreDelta = result.new_score - result.score_before;
  const hasBigWin = deep && (deep.finances?.length > 0 || deep.dirigeants?.length > 0);

  return (
    <div className="space-y-3" data-testid="enrich-results">
      {/* Header */}
      <Reveal delay={0} testId="reveal-header">
        <div className="rounded-xl p-5 border border-emerald-500/40 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, rgba(0,220,130,0.22), rgba(0,220,130,0.05))" }}>
          <div className="hp-label flex items-center gap-1.5" style={{ color: "#86efac" }}>
            <Sparkles className="w-3 h-3" /> Magic Prosperr
          </div>
          <div className="font-display text-3xl font-semibold text-white mt-2 leading-none tracking-tight">
            {hasBigWin ? "Données publiques trouvées" : (Object.keys(result.suggested || {}).length === 0 ? "Aucune nouvelle donnée" : `${Object.keys(result.suggested).length} info${Object.keys(result.suggested).length > 1 ? "s" : ""}`)}
          </div>
          {scoreDelta > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1 bg-emerald-500/15 border border-emerald-500/40">
              <Trophy className="w-3.5 h-3.5 text-emerald-300" />
              <span className="text-xs font-semibold text-emerald-200">+{scoreDelta} points de qualification</span>
            </div>
          )}
        </div>
      </Reveal>

      {deep?.health && (
        <Reveal delay={150} testId="reveal-health">
          <HealthCard health={deep.health} identity={deep.identity} effectifs={deep.effectifs} />
        </Reveal>
      )}

      {deep?.finances && deep.finances.length > 0 && (
        <Reveal delay={300} testId="reveal-finances">
          <FinancesCard finances={deep.finances} />
        </Reveal>
      )}

      {deep?.dirigeants && deep.dirigeants.length > 0 && (
        <Reveal delay={450} testId="reveal-dirigeants">
          <DirigeantsCard dirigeants={deep.dirigeants} />
        </Reveal>
      )}

      {!deep && Object.keys(result.suggested || {}).length > 0 && (
        <Reveal delay={150}>
          <div className="hp-card overflow-hidden divide-y divide-[var(--border-soft)]" data-testid="enrich-suggestions">
            {Object.entries(result.suggested).map(([k, v]) => (
              <div key={k} className="p-3 flex items-center gap-3">
                <div className="hp-label w-24 shrink-0">{FIELD_LABELS[k] || k}</div>
                <div className="text-sm font-medium text-[var(--text)] truncate flex-1">{String(v)}</div>
              </div>
            ))}
          </div>
        </Reveal>
      )}

      {result.sources_used?.length > 0 && (
        <Reveal delay={550}>
          <div className="text-[11px] text-zinc-500 px-1">
            Sources : {result.sources_used.join(" · ")}
          </div>
        </Reveal>
      )}

      <Reveal delay={650}>
        <div className="flex gap-2 pt-1">
          <button onClick={onDismiss} className="hp-btn hp-btn-secondary flex-1 h-12 text-sm">
            Ignorer
          </button>
          <button onClick={onApply} disabled={applying} data-testid="enrich-apply"
            className="hp-btn hp-btn-primary flex-1 h-12 text-sm">
            {applying ? "Enregistrement…" : "Enregistrer dans la fiche"}
          </button>
        </div>
      </Reveal>
    </div>
  );
}

function HealthCard({ health, identity, effectifs }) {
  const meta = HEALTH_META[health.status] || HEALTH_META.unknown;
  const Icon = meta.Icon;
  return (
    <div className={`rounded-xl border p-4 ${meta.border} ${meta.bg}`} data-testid="health-card">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-lg border ${meta.border} grid place-items-center shrink-0 bg-[var(--bg)]/40`}>
          <Icon className={`w-5 h-5 ${meta.cls}`} strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="hp-label">Santé financière</div>
          <div className={`font-display font-semibold text-lg flex items-center gap-2 mt-0.5 ${meta.cls}`}>
            {meta.label}
            {health.trend_pct !== null && health.trend_pct !== undefined && (
              <span className="text-sm tabular-nums">
                {health.trend_pct > 0 ? "+" : ""}{health.trend_pct}%
              </span>
            )}
          </div>
        </div>
      </div>
      {(identity?.annees_activite || effectifs?.tranche || identity?.capital_formate) && (
        <div className="mt-4 pt-4 border-t border-[var(--border-soft)] grid grid-cols-3 gap-3 text-center">
          {identity?.annees_activite != null && (
            <div>
              <div className="font-display text-xl font-semibold text-[var(--text)] tabular-nums">{identity.annees_activite}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">An{identity.annees_activite > 1 ? "s" : ""} d'activité</div>
            </div>
          )}
          {effectifs?.tranche && (
            <div>
              <div className="font-display text-sm font-semibold text-[var(--text)] leading-tight">{effectifs.tranche}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">Effectif</div>
            </div>
          )}
          {identity?.capital_formate && (
            <div>
              <div className="font-display text-sm font-semibold text-[var(--text)] leading-tight">{identity.capital_formate}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">Capital</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FinancesCard({ finances }) {
  const latest = finances[0];
  const max = Math.max(...finances.map((f) => f.ca || 0), 1);
  return (
    <div className="hp-card p-4" data-testid="finances-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 grid place-items-center shrink-0">
          <BadgeEuro className="w-4 h-4 text-emerald-300" strokeWidth={1.8} />
        </div>
        <div className="flex-1">
          <div className="hp-label">Chiffre d'affaires</div>
          <div className="font-display text-3xl font-semibold text-[var(--text)] tabular-nums leading-none mt-1">
            {latest.ca_label}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">Exercice {latest.annee}</div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3 items-end h-20">
        {[...finances].reverse().map((f, i) => {
          const height = max > 0 ? (f.ca / max) * 100 : 0;
          const isLatest = i === finances.length - 1;
          return (
            <div key={f.annee || i} className="flex flex-col items-center gap-1 h-full">
              <div className="flex-1 w-full flex items-end">
                <div
                  className={`w-full rounded-sm ${isLatest ? "bg-emerald-500" : "bg-emerald-500/30"} transition-all`}
                  style={{ height: `${Math.max(5, height)}%` }}
                />
              </div>
              <div className="text-[10px] text-zinc-500 tabular-nums mt-1">{f.annee}</div>
              <div className="text-[10px] font-semibold text-[var(--text)] tabular-nums">{f.ca_label}</div>
            </div>
          );
        })}
      </div>
      {latest.resultat_label && (
        <div className="mt-3 pt-3 border-t border-[var(--border-soft)] flex items-center justify-between text-xs">
          <span className="text-zinc-500">Résultat net {latest.annee}</span>
          <span className={`font-semibold tabular-nums ${latest.resultat < 0 ? "text-rose-300" : "text-[var(--text)]"}`}>
            {latest.resultat_label}
          </span>
        </div>
      )}
    </div>
  );
}

function DirigeantsCard({ dirigeants }) {
  return (
    <div className="hp-card overflow-hidden" data-testid="dirigeants-card">
      <div className="p-4 flex items-center gap-3 border-b border-[var(--border-soft)]">
        <div className="w-10 h-10 rounded-lg bg-sky-500/15 border border-sky-500/30 grid place-items-center shrink-0">
          <Users className="w-4 h-4 text-sky-300" strokeWidth={1.8} />
        </div>
        <div>
          <div className="hp-label">Dirigeants</div>
          <div className="font-display font-semibold text-lg text-[var(--text)]">{dirigeants.length} identifié{dirigeants.length > 1 ? "s" : ""}</div>
        </div>
      </div>
      <div className="divide-y divide-[var(--border-soft)]">
        {dirigeants.map((d, i) => (
          <div key={i} className="p-3.5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 grid place-items-center font-display font-semibold text-xs shrink-0">
              {(d.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-[var(--text)] truncate">{d.name}</div>
              {d.qualite && <div className="text-xs text-zinc-500 truncate">{d.qualite}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PappersDetailsCard({ data, onRefresh, loading }) {
  return (
    <div className="px-5 mt-4 space-y-3">
      <div className="flex items-center justify-between px-1">
        <div className="hp-label flex items-center gap-1.5" style={{ color: "#86efac" }}>
          <Sparkles className="w-3 h-3" /> Magic Prosperr
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          data-testid="magic-refresh"
          className="text-[11px] font-semibold text-zinc-400 hover:text-emerald-300 disabled:opacity-50 flex items-center gap-1 transition-colors"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          Actualiser
        </button>
      </div>
      {data.health && <HealthCard health={data.health} identity={data.identity} effectifs={data.effectifs} />}
      {data.finances && data.finances.length > 0 && <FinancesCard finances={data.finances} />}
      {data.dirigeants && data.dirigeants.length > 0 && <DirigeantsCard dirigeants={data.dirigeants} />}
    </div>
  );
}
